import { type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './cn';

export interface TabItem<T extends string = string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface TabsProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  tabs: TabItem<T>[];
  ariaLabel?: string;
  className?: string;
}

export function Tabs<T extends string = string>({
  value,
  onChange,
  tabs,
  ariaLabel,
  className,
}: TabsProps<T>) {
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    for (let step = 1; step <= tabs.length; step++) {
      const nextIndex = (index + dir * step + tabs.length) % tabs.length;
      const next = tabs[nextIndex];
      if (!next.disabled) {
        onChange(next.value);
        break;
      }
    }
  };

  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('ui-tabs', className)}>
      {tabs.map((tab, i) => {
        const isSelected = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={isSelected}
            tabIndex={isSelected ? 0 : -1}
            disabled={tab.disabled}
            className={cn('ui-tabs__tab', isSelected && 'ui-tabs__tab--active')}
            onClick={() => {
              if (!tab.disabled) onChange(tab.value);
            }}
            onKeyDown={e => handleKeyDown(e, i)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
